import { TArticle, TWordPressPost } from '@/types/blog'

const CATEGORY_MAP: Record<number, { title: string; seo_alias: string }> = {
  125: {
    title: 'Bản tin hàng tháng',
    seo_alias: 'ban-tin-hang-thang',
  },
  126: {
    title: 'Tin tức và hoạt động',
    seo_alias: 'tin-tuc-va-hoat-dong',
  },
}

/**
 * Strip HTML tags from a string
 */
const stripHtml = (html: string): string => {
  if (typeof window !== 'undefined') {
    const tmp = document.createElement('DIV')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }
  // Server-side: remove HTML tags and decode common entities
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&[^;]+;/g, '')
    .trim()
}

/**
 * Extract seo_alias from WordPress post link URL
 * Example: https://www.coolmate.me/blog/ban-tin-coolmate-thang-8-nam-2025 -> ban-tin-coolmate-thang-8-nam-2025
 */
const extractSeoAlias = (link: string): string => {
  try {
    const url = new URL(link)
    const pathParts = url.pathname.split('/').filter(Boolean)
    // Find 'blog' in path and get the next part
    const blogIndex = pathParts.indexOf('blog')
    if (blogIndex !== -1 && blogIndex < pathParts.length - 1) {
      return pathParts[blogIndex + 1]
    }
    // Fallback: get last part of path
    return pathParts[pathParts.length - 1] || ''
  } catch {
    // Fallback: extract from pathname string
    const match = link.match(/\/blog\/([^\/]+)/)
    return match ? match[1] : ''
  }
}

/**
 * Transform WordPress post to TArticle format
 */
export const formatWordPressPostToArticle = (
  post: TWordPressPost,
  categoryId: number
): TArticle => {
  const category = CATEGORY_MAP[categoryId] || {
    title: 'Tin tức',
    seo_alias: 'tin-tuc',
  }

  const seoAlias = extractSeoAlias(post.link)
  const excerpt = stripHtml(post.excerpt.rendered)

  // Map amlab_tham_khao to file_share if available
  const fileShare = post.amlab_tham_khao?.[0]?.amlab_link_tk
    ? [
        {
          type: 'link',
          url: post.amlab_tham_khao[0].amlab_link_tk,
        },
      ]
    : []

  return {
    _id: String(post.id),
    images: post.featured_image_url ? [post.featured_image_url] : [],
    title: post.title.rendered,
    excerpt,
    blog_ids: [],
    seo_title: post.title.rendered,
    seo_description: excerpt,
    seo_keywords: '',
    seo_alias: seoAlias,
    link: post.link,
    available: post.status === 'publish',
    author: '',
    tags: [],
    file_share: fileShare,
    status: post.status,
    top_view_orderby: 0,
    view_count: 0,
    article_id: post.id,
    updated_at: post.modified,
    created_at: post.modified,
    on_slides: false,
    modified_date: post.modified,
    blogs: [
      {
        _id: String(categoryId),
        title: category.title,
        seo_alias: category.seo_alias,
      },
    ],
    view: 0,
    creator: {
      _id: '',
      id: '',
      email: '',
      gender: '',
      birthday: '',
      fullname: '',
      role: '',
      phone: '',
      facebook: '',
      image_url: '',
      description: '',
      updated_at: '',
      created_at: '',
      activation_code: '',
      pwd_change_require: false,
      instagram: '',
      deleted: 0,
    },
  }
}

/**
 * Transform array of WordPress posts to TArticle array
 */
export const formatWordPressPostsToArticles = (
  posts: TWordPressPost[],
  categoryId: number
): TArticle[] => {
  return posts.map((post) => formatWordPressPostToArticle(post, categoryId))
}

